import { defaultLanguage, getLanguageMeta, normalizeI18nText, supportedLanguages, translations } from './translations';

const STORAGE_KEY = 'interviewgenie_locale';
const MISSING_STORAGE_KEY = 'interviewgenie_missing_i18n_phrases';
const TEXT_NODE_ORIGINALS = new WeakMap();
const ATTRIBUTE_NAMES = ['placeholder', 'title', 'aria-label', 'alt', 'value'];
const ATTRIBUTE_ORIGINAL_KEY = 'i18nOriginal';
const SKIP_SELECTOR = [
  '[data-i18n-ignore]',
  'script',
  'style',
  'noscript',
  'code',
  'pre',
  'textarea',
  'input[type="password"]',
].join(',');

let currentLanguage = defaultLanguage;
let observer = null;
let scheduled = false;
let isApplying = false;
let floatingSwitcherMounted = false;
const listeners = new Set();

function hasWindow() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isSupportedLanguage(lang) {
  return supportedLanguages.some((item) => item.code === lang);
}

function getInitialLanguage() {
  if (!hasWindow()) return defaultLanguage;
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (isSupportedLanguage(stored)) return stored;

  const browserLanguage = window.navigator?.language?.slice(0, 2);
  if (isSupportedLanguage(browserLanguage)) return browserLanguage;
  return defaultLanguage;
}

function preserveWhitespace(original, translated) {
  const start = original.match(/^\s*/)?.[0] || '';
  const end = original.match(/\s*$/)?.[0] || '';
  return `${start}${translated}${end}`;
}

function translateExact(normalized, lang) {
  if (!normalized || lang === defaultLanguage) return normalized;
  return translations[lang]?.[normalized] || normalized;
}

function translateWithFallbacks(text, lang) {
  const normalized = normalizeI18nText(text);
  if (!normalized || lang === defaultLanguage) return text;

  let translated = translateExact(normalized, lang);

  // Support common dynamic messages like: "AI Chat requires an upgrade".
  if (translated === normalized) {
    translated = normalized
      .replace(/(.+) requires an upgrade$/i, (_, feature) => {
        const featureName = translateExact(normalizeI18nText(feature), lang);
        return lang === 'ar' ? `${featureName} يتطلب ترقية` : `${featureName} nécessite une mise à niveau`;
      })
      .replace(/(.+) is locked on the Free plan\.$/i, (_, feature) => {
        const featureName = translateExact(normalizeI18nText(feature), lang);
        return lang === 'ar' ? `${featureName} مقفل في الخطة المجانية.` : `${featureName} est verrouillé dans le plan gratuit.`;
      });
  }

  if (translated === normalized) recordMissingPhrase(normalized);
  return preserveWhitespace(text, translated);
}

function recordMissingPhrase(phrase) {
  if (!hasWindow() || !phrase || phrase.length < 2 || phrase.length > 180) return;
  if (!/[A-Za-z]/.test(phrase)) return;

  try {
    const current = JSON.parse(window.localStorage.getItem(MISSING_STORAGE_KEY) || '[]');
    if (!current.includes(phrase)) {
      const next = [...current, phrase].slice(-500);
      window.localStorage.setItem(MISSING_STORAGE_KEY, JSON.stringify(next));
    }
  } catch {
    // Ignore storage failures.
  }
}

function shouldSkipElement(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return false;
  return Boolean(element.closest(SKIP_SELECTOR));
}

function translateTextNode(node, lang) {
  const raw = node.nodeValue || '';
  if (!normalizeI18nText(raw)) return;
  if (!TEXT_NODE_ORIGINALS.has(node)) TEXT_NODE_ORIGINALS.set(node, raw);
  const original = TEXT_NODE_ORIGINALS.get(node);
  const translated = translateWithFallbacks(original, lang);
  if (node.nodeValue !== translated) node.nodeValue = translated;
}

function translateAttributes(element, lang) {
  ATTRIBUTE_NAMES.forEach((attributeName) => {
    if (!element.hasAttribute?.(attributeName)) return;
    if (attributeName === 'value') {
      const tagName = element.tagName?.toLowerCase();
      const inputType = element.getAttribute('type');
      const isButtonLike = tagName === 'button' || inputType === 'button' || inputType === 'submit' || inputType === 'reset';
      if (!isButtonLike) return;
    }

    const originalDatasetKey = `${ATTRIBUTE_ORIGINAL_KEY}${attributeName.replace(/(^|-)([a-z])/g, (_, __, letter) => letter.toUpperCase())}`;
    const currentValue = element.getAttribute(attributeName);
    if (!currentValue || !normalizeI18nText(currentValue)) return;
    if (!element.dataset[originalDatasetKey]) element.dataset[originalDatasetKey] = currentValue;
    const translated = translateWithFallbacks(element.dataset[originalDatasetKey], lang);
    if (currentValue !== translated) element.setAttribute(attributeName, translated);
  });
}

function walkAndTranslate(root, lang) {
  if (!hasWindow() || !root) return;
  const nodeType = root.nodeType;

  if (nodeType === Node.TEXT_NODE) {
    const parent = root.parentElement;
    if (!shouldSkipElement(parent)) translateTextNode(root, lang);
    return;
  }

  if (nodeType !== Node.ELEMENT_NODE && nodeType !== Node.DOCUMENT_NODE && nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;

  if (root.nodeType === Node.ELEMENT_NODE) {
    if (shouldSkipElement(root)) return;
    translateAttributes(root, lang);
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE && shouldSkipElement(node)) return NodeFilter.FILTER_REJECT;
      if (node.nodeType === Node.TEXT_NODE && shouldSkipElement(node.parentElement)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let node = walker.currentNode;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) translateTextNode(node, lang);
    if (node.nodeType === Node.ELEMENT_NODE) translateAttributes(node, lang);
    node = walker.nextNode();
  }
}

function applyDirection(lang) {
  if (!hasWindow()) return;
  const meta = getLanguageMeta(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = meta.dir;
  document.body?.setAttribute('dir', meta.dir);
  document.body?.classList.toggle('ig-rtl', meta.dir === 'rtl');
}

function injectBaseStyles() {
  if (!hasWindow() || document.getElementById('ig-i18n-runtime-styles')) return;
  const style = document.createElement('style');
  style.id = 'ig-i18n-runtime-styles';
  style.textContent = `
    html[dir="rtl"] body { text-align: right; }
    html[dir="rtl"] [data-keep-ltr], html[dir="rtl"] input[type="email"], html[dir="rtl"] input[type="url"], html[dir="rtl"] input[type="tel"] { direction: ltr; text-align: left; }
    #ig-i18n-floating-language-switcher { position: fixed; right: 18px; bottom: 18px; z-index: 2147482000; display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid rgba(255,255,255,.14); border-radius: 999px; background: rgba(7,9,12,.78); color: #fff; backdrop-filter: blur(18px); box-shadow: 0 18px 50px rgba(0,0,0,.35); font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
    #ig-i18n-floating-language-switcher select { background: rgba(255,255,255,.08); color: #fff; border: 1px solid rgba(255,255,255,.12); border-radius: 999px; padding: 4px 8px; font-size: 12px; outline: none; }
    #ig-i18n-floating-language-switcher option { color: #0b0b12; }
    html[dir="rtl"] #ig-i18n-floating-language-switcher { left: 18px; right: auto; }
  `;
  document.head.appendChild(style);
}

function applyLanguage(lang = currentLanguage) {
  if (!hasWindow()) return;
  isApplying = true;
  try {
    applyDirection(lang);
    walkAndTranslate(document.body, lang);
  } finally {
    isApplying = false;
  }
}

function scheduleApply() {
  if (!hasWindow() || scheduled || isApplying) return;
  scheduled = true;
  window.requestAnimationFrame(() => {
    scheduled = false;
    applyLanguage(currentLanguage);
  });
}

function setupObserver() {
  if (!hasWindow() || observer || !document.body) return;
  observer = new MutationObserver((mutations) => {
    if (isApplying) return;
    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.type === 'characterData' || mutation.type === 'attributes')) {
      scheduleApply();
    }
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRIBUTE_NAMES,
  });
}

function removeFloatingSwitcherWhenEmbeddedExists() {
  if (!hasWindow()) return;
  const embeddedSwitcher = document.querySelector('[data-i18n-language-switcher]');
  const floatingSwitcher = document.getElementById('ig-i18n-floating-language-switcher');
  if (embeddedSwitcher && floatingSwitcher) {
    floatingSwitcher.remove();
    floatingSwitcherMounted = false;
  }
}

function mountFloatingSwitcher() {
  if (!hasWindow() || floatingSwitcherMounted) return;
  if (document.querySelector('[data-i18n-language-switcher]')) return;
  if (window.localStorage?.getItem('interviewgenie_hide_floating_locale_switcher') === 'true') return;

  const mount = () => {
    if (floatingSwitcherMounted || document.querySelector('[data-i18n-language-switcher]')) return;
    if (document.querySelector('[data-i18n-language-switcher]')) return;
    const wrapper = document.createElement('div');
    wrapper.id = 'ig-i18n-floating-language-switcher';
    wrapper.setAttribute('data-i18n-ignore', 'true');
    wrapper.innerHTML = `
      <span aria-hidden="true">🌐</span>
      <select aria-label="Language">
        ${supportedLanguages.map((lang) => `<option value="${lang.code}">${lang.nativeLabel}</option>`).join('')}
      </select>
    `;
    const select = wrapper.querySelector('select');
    select.value = currentLanguage;
    select.addEventListener('change', (event) => setLanguage(event.target.value));
    document.body.appendChild(wrapper);
    floatingSwitcherMounted = true;
  };

  window.setTimeout(mount, 800);
}

export function initI18nRuntime() {
  if (!hasWindow()) return;
  currentLanguage = getInitialLanguage();
  injectBaseStyles();

  const start = () => {
    applyLanguage(currentLanguage);
    setupObserver();
    removeFloatingSwitcherWhenEmbeddedExists();
    mountFloatingSwitcher();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

export function getLanguage() {
  return currentLanguage;
}

export function setLanguage(lang) {
  if (!isSupportedLanguage(lang)) return currentLanguage;
  currentLanguage = lang;
  if (hasWindow()) {
    window.localStorage?.setItem(STORAGE_KEY, lang);
    window.dispatchEvent(new CustomEvent('interviewgenie:language-change', { detail: { language: lang } }));
  }
  applyLanguage(lang);
  listeners.forEach((listener) => listener(lang));
  return currentLanguage;
}

export function subscribeToLanguage(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function t(key, fallback = key, lang = currentLanguage) {
  const normalized = normalizeI18nText(key);
  const translated = translateExact(normalized, lang);
  return translated === normalized ? fallback : translated;
}

export function getMissingPhrases() {
  if (!hasWindow()) return [];
  try {
    return JSON.parse(window.localStorage.getItem(MISSING_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export { supportedLanguages };

if (hasWindow()) {
  window.InterviewGenieI18n = {
    getLanguage,
    setLanguage,
    t,
    supportedLanguages,
    getMissingPhrases,
  };
}

initI18nRuntime();
